import React, { lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import heroBanner from '../assets/images/احذفها_وايضا_cta_buttons_وايضا_202605132241.jpeg';

const GradeCards = lazy(() => import('../components/common').then(module => ({ default: module.GradeCards })));

const Home: React.FC = () => {
  const { isAuthenticated } = useAuth();

  const scrollToDiscoverPlatform = () => {
    document.getElementById('discover-platform')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-emerald-50/30 to-white" dir="rtl">
      <section
        className="relative min-h-[calc(100svh-4rem)] w-full overflow-hidden"
        aria-label="الصفحة الرئيسية"
      >
        <img
          src={heroBanner}
          alt="أكاديمية الفاروق للعلوم الشرعية"
          className="absolute inset-0 h-full w-full object-cover object-center"
          loading="eager"
          fetchPriority="high"
        />
        <div
          className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/15 to-black/25 pointer-events-none"
          aria-hidden
        />
        <div className="relative z-10 flex min-h-[calc(100svh-4rem)] flex-col items-center justify-end pb-10 pt-8 px-4 sm:pb-14 md:pb-20">
          <div className="flex w-full max-w-lg flex-col items-stretch justify-center gap-3 sm:max-w-none sm:flex-row sm:items-center sm:justify-center sm:gap-4">
            {!isAuthenticated && (
              <Link
                to="/register"
                className="inline-flex items-center justify-center rounded-xl px-8 py-3.5 text-base font-bold text-emerald-950 bg-gradient-to-r from-amber-400 via-amber-500 to-yellow-500 shadow-lg shadow-black/30 ring-2 ring-amber-200/50 transition hover:brightness-105 hover:scale-[1.02] active:scale-[0.98] font-cairo"
              >
                أنشئ حساب
              </Link>
            )}
            <button
              type="button"
              onClick={scrollToDiscoverPlatform}
              className="inline-flex items-center justify-center rounded-xl px-8 py-3.5 text-base font-bold text-white bg-emerald-900/55 backdrop-blur-md border-2 border-amber-400/70 shadow-lg transition hover:bg-emerald-900/75 hover:scale-[1.02] active:scale-[0.98] font-cairo"
            >
              استكشف المنصة
            </button>
          </div>
        </div>
      </section>

      <section id="discover-platform" className="relative py-8 sm:py-16 md:py-20 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block">
              <h2 className="font-el-messiri text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-4">
                لماذا <span className="text-emerald-700">أكاديمية الفاروق</span>؟
              </h2>
              <div className="h-1 bg-gradient-to-r from-transparent via-emerald-600 to-transparent"></div>
            </div>
            <p className="font-cairo text-lg sm:text-xl text-slate-600 mt-6 max-w-2xl mx-auto">
              علمٌ يرتقي بك دينًا ودنيا — منظم، موثّق، وقريب من سؤالك
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            <div className="flex flex-col justify-center w-full h-auto text-center p-6 bg-emerald-50 space-y-4 text-black shadow-[0_4px_20px_rgba(0,0,0,0.08)] transition-colors duration-300 rounded-xl hover:bg-emerald-100/90 border border-emerald-100">
              <div className="flex flex-col-reverse items-center justify-center">
                <h3 className="font-el-messiri text-2xl font-bold text-slate-900">محتوى متكامل</h3>
                <span className="mb-4">
                  <svg className="h-10 w-10 text-emerald-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </span>
              </div>
              <p className="font-cairo text-center w-full text-slate-700 text-sm">
                دروس ومسارات في العقيدة والفقه والسيرة والحديث — تشاهدها في الوقت الذي يناسبك
              </p>
            </div>

            <div className="flex flex-col justify-center w-full h-auto text-center p-6 bg-emerald-50 space-y-4 text-black shadow-[0_4px_20px_rgba(0,0,0,0.08)] transition-colors duration-300 rounded-xl hover:bg-emerald-100/90 border border-emerald-100">
              <div className="flex flex-col-reverse items-center justify-center">
                <h3 className="font-el-messiri text-2xl font-bold text-slate-900">متابعة تقدمك</h3>
                <span className="mb-4">
                  <svg className="h-10 w-10 text-emerald-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
                  </svg>
                </span>
              </div>
              <p className="font-cairo text-center w-full text-slate-700 text-sm">
                لوحة تساعدك على معرفة ما أنجزته وما تبقّى — لتبقى على خطى ثابتة في طلب العلم
              </p>
            </div>

            <div className="flex flex-col justify-center w-full h-auto text-center p-6 bg-emerald-50 space-y-4 text-black shadow-[0_4px_20px_rgba(0,0,0,0.08)] transition-colors duration-300 rounded-xl hover:bg-emerald-100/90 border border-emerald-100">
              <div className="flex flex-col-reverse items-center justify-center">
                <h3 className="font-el-messiri text-2xl font-bold text-slate-900">شرح واضح</h3>
                <span className="mb-4">
                  <svg className="h-10 w-10 text-emerald-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
                  </svg>
                </span>
              </div>
              <p className="font-cairo text-center w-full text-slate-700 text-sm">
                تبسيط دون تسطيح — يقرّب المفهوم ويثبت الأدلة من الكتاب والسنة
              </p>
            </div>

            <div className="flex flex-col justify-center w-full h-auto text-center p-6 bg-emerald-50 space-y-4 text-black shadow-[0_4px_20px_rgba(0,0,0,0.08)] transition-colors duration-300 rounded-xl hover:bg-emerald-100/90 border border-emerald-100">
              <div className="flex flex-col-reverse items-center justify-center">
                <h3 className="font-el-messiri text-2xl font-bold text-slate-900">مراجعة وتقويم</h3>
                <span className="mb-4">
                  <svg className="h-10 w-10 text-emerald-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
                  </svg>
                </span>
              </div>
              <p className="font-cairo text-center w-full text-slate-700 text-sm">
                أسئلة وتمارين تساعدك على مراجعة ما تعلّمته وتثبيته في الذاكرة
              </p>
            </div>
          </div>

          <Suspense fallback={<div className="text-center py-8 font-cairo text-slate-600">جاري التحميل...</div>}>
            <div id="courses-section">
              <GradeCards />
            </div>
          </Suspense>
        </div>
      </section>
    </div>
  );
};

export default Home;
